<template>
    <div v-if="isOpen" class="fixed inset-0 z-9999 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div class="w-full max-w-md rounded-xl bg-white dark:bg-slate-800 p-6 shadow-2xl border border-slate-200 dark:border-slate-700">
        
        <div class="text-center mb-6">
          <div class="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/10 text-amber-500">
            <i class="pi pi-lock text-xl"></i>
          </div>
          <h3 class="text-lg font-bold text-slate-900 dark:text-white">Session Expired</h3>
          <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">Please enter your password</p>
        </div>
  
        <form @submit.prevent="handleReAuth" class="space-y-4">
          <div>
            <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Password</label>
            <input 
              v-model="password" 
              type="password" 
              required 
              class="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
  
          <div v-if="errorMessage" class="text-xs text-rose-500 text-center font-medium">
            {{ errorMessage }}
          </div>
  
          <div class="flex gap-2 pt-2">
            <button 
              type="button" 
              @click="$emit('logout')" 
              class="w-1/2 rounded-lg border border-slate-300 dark:border-slate-700 px-4 py-2 text-xs font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              Logout
            </button>
            
            <button 
              type="submit" 
              :disabled="isLoading" 
              class="w-1/2 rounded-lg bg-blue-600 px-4 py-2 text-xs font-semibold text-white hover:bg-blue-500 transition-colors disabled:opacity-50"
            >
              {{ isLoading ? 'Verifying...' : 'Continue' }}
            </button>
          </div>
        </form>
  
      </div>
    </div>
  </template>
  
  <script setup>
  import { ref }       from 'vue'
  import CryptoJS      from 'crypto-js'
  import { jwtDecode } from 'jwt-decode'

  // XSS Sanitization
  import DOMPurify from 'dompurify'
  
  const props = defineProps({
    isOpen: Boolean,
    userName: String
  })

  function sanitizeInput(val) {
    if (typeof val === 'string') {
        return DOMPurify.sanitize(val.trim())
    }
    return val
  }
  
  const emit = defineEmits(['reauthenticated', 'logout'])
  
  const password = ref('')
  const isLoading = ref(false)
  const errorMessage = ref('')
  
  async function handleReAuth() {
    isLoading.value = true
    errorMessage.value = ''

    const payload = new URLSearchParams()

    // Check XSS here __________________________
    payload.append('username'   , sanitizeInput( props.userName )?? "")
    payload.append('password'   , sanitizeInput( password.value )?? "")
  
    try {

      // Try to HIDE THIS REQUEST ____________________
      const SECRET_KEY       = import.meta.env.VITE_APP_SECRET
      const SECRET_URL       = import.meta.env.VITE_URL_SECRET
      const endpoint         = "/api/auth/login"
      const encryptedTarget  = CryptoJS.AES.encrypt( endpoint , SECRET_URL).toString();
      const encryptedUrl     = CryptoJS.AES.encrypt( endpoint , SECRET_KEY).toString();
      const rawPayload       = payload.toString()
      const encryptedPayload = CryptoJS.AES.encrypt(rawPayload, SECRET_KEY).toString();

      // Base64 encrypted Url to avoid "/" character, which is not allowed in URL
      const targeturl        =  '/api/4.0.4/' + btoa(encodeURIComponent(encryptedTarget)) 
      const baseUrl          =  import.meta.env.VITE_PROXY_URL
      
      // Try to SECRET LOGIN REQUEST _______________________
      const res = await fetch(`${baseUrl}/secure-proxy`, {
        method : 'POST',                                    // Force POST so params stay hidden in the body
        headers: { 'Content-Type' : 'application/json' },
        body   : JSON.stringify({
            t  : encryptedUrl
          , m  : "POST"
          , p  : encryptedPayload
        })
      });
  
      const data = await res.json()
  
      if (res.ok && data.success) {
        const token = data.data?.token
        const decoded = jwtDecode(token)
        const expiresAt = decoded.exp * 1000
  
        localStorage.setItem('auth_token', token)
        localStorage.setItem('session_expires_at', expiresAt.toString())
  
        password.value = ''
        emit('reauthenticated')
      } else {
        errorMessage.value = data.message || 'Invalid password.'
      }
    } catch (err) {
      console.log("🚀 ~ handleReAuth ~ err:", err)
      errorMessage.value = 'Failed to re-authenticate.'
    } finally {
      isLoading.value = false
    }
  }
  </script>